import Grid from "@mui/material/Grid";
import {
  Autocomplete,
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import React, { useEffect, useState } from "react";

import QuizGroupSummaryCard from "./QuizGroupSummaryCard";
import { PublishedOrDraftToggle } from "../../../shared-components/PublishedOrDraftToggle";
import { MyClassesOrAllClassesToggle } from "../../../shared-components/MyClassesOrAllClassesToggle";
import { useApiClient } from "../../../api";
import { useAuthenticationStore } from "../../../stores/auth";
import { usePagination } from "../../../hooks/usePagination";
import GeneralLoading from "../../../shared-components/Loading";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";

const SEARCH_DEBOUNCE_MS = 200;

export const FruitifyQuizSearch = ({
  forSchoolClass = null,
  excludeTitle = false,
  showAnswerCount = false,
  studentView = false,
}) => {
  const statusOptions = ["Upcoming", "Past Deadline"];

  const ApiClient = useApiClient();
  const { user } = useAuthenticationStore();

  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedValue, setDebouncedValue] = useState("");

  const [selectedClass, setSelectedClass] = useState(forSchoolClass || null);

  const [classSearchTerm, setClassSearchTerm] = useState("");

  const [debouncedClassSearchTerm, setDebouncedClassSearchTerm] = useState("");

  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);

  const [published, setPublished] = useState(true);

  const [showAllClasses, setShowAllClasses] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedClassSearchTerm(classSearchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(handler);
    };
  }, [classSearchTerm]);

  const {
    options: schoolClasses,
    loading: schoolClassesLoading,
    pageInfo: schoolClassesPageInfo,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.schools.getSchoolClasses({
        userRole: user.info.currentRole,
        usersOnly: !showAllClasses,
        showAllClasses,
        searchTerm: debouncedClassSearchTerm,
        first,
        after,
      }),
    20,
    {
      userRole: user.info.currentRole,
      showAllClasses,
      debouncedClassSearchTerm,
    },
    !forSchoolClass,
  );

  useEffect(() => {
    if (forSchoolClass) {
      setSelectedClass(forSchoolClass);
    }
  }, [forSchoolClass]);

  useEffect(() => {
    if (
      forSchoolClass ||
      debouncedClassSearchTerm ||
      schoolClassesLoading ||
      !schoolClassesPageInfo
    ) {
      return;
    }

    if (schoolClasses.length === 1 && !schoolClassesPageInfo.hasNextPage) {
      setSelectedClass(schoolClasses[0]);
    }
  }, [
    forSchoolClass,
    debouncedClassSearchTerm,
    schoolClassesLoading,
    schoolClasses,
    schoolClassesPageInfo,
  ]);

  useEffect(() => {
    if (forSchoolClass) {
      return;
    }

    setSelectedClass(null);
    setClassSearchTerm("");
    setDebouncedClassSearchTerm("");
  }, [showAllClasses, forSchoolClass]);

  const {
    options: quizGroups,
    page,
    pageInfo,
    handlePageChange,
    loading,
  } = usePagination(
    ({ first, after }) =>
      ApiClient.quiz.getAllQuizGroups({
        schoolClass: selectedClass?.id || null,
        isPublished: published,
        showAllClasses,
        searchString: debouncedValue,
        selectedStatus,
        first,
        after,
      }),
    12,
    {
      selectedClass,
      published,
      showAllClasses,
      debouncedValue,
      selectedStatus,
    },
  );

  const classOptions = forSchoolClass ? [forSchoolClass] : schoolClasses;

  return (
    <Grid
      container
      spacing={2}
      sx={{
        width: "100%",
        mt: 6,
      }}
      data-testid="fruitify-quiz-group-search"
    >
      {!excludeTitle && (
        <Grid size={12} sx={{ mb: 2 }}>
          <Typography variant="h2">Quizzes</Typography>
        </Grid>
      )}

      <Grid size={12} sx={{ mb: 2 }}>
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: 50,
            },
          }}
        >
          <TextField
            label="Search Keyword"
            variant="outlined"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 240,
              },
            }}
          />

          <Autocomplete
            options={classOptions}
            value={selectedClass}
            loading={schoolClassesLoading}
            disabled={Boolean(forSchoolClass)}
            disableClearable={Boolean(forSchoolClass)}
            filterOptions={(options) => options}
            onInputChange={(_event, value, reason) => {
              if (reason === "input") {
                setClassSearchTerm(value);

                if (selectedClass && value !== selectedClass.name) {
                  setSelectedClass(null);
                }
              }

              if (reason === "clear") {
                setClassSearchTerm("");
              }
            }}
            onChange={(_event, schoolClass) => {
              setSelectedClass(schoolClass);

              setClassSearchTerm("");
              setDebouncedClassSearchTerm("");
            }}
            getOptionLabel={(schoolClass) => schoolClass?.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={
              schoolClassesLoading ? "Loading classes..." : "No classes found"
            }
            renderInput={(params) => (
              <TextField {...params} label="Class" placeholder="All classes" />
            )}
            sx={{
              width: {
                xs: "100%",
                sm: 240,
              },
            }}
          />

          {published && (
            <FormControl
              sx={{
                width: {
                  xs: "100%",
                  sm: 240,
                },
              }}
            >
              <InputLabel id="status-label">Status</InputLabel>

              <Select
                labelId="status-label"
                value={selectedStatus}
                label="Status"
                onChange={(event) => setSelectedStatus(event.target.value)}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {!studentView && !forSchoolClass && (
            <MyClassesOrAllClassesToggle
              showAllClasses={showAllClasses}
              setShowAllClasses={setShowAllClasses}
            />
          )}

          {!studentView && (
            <PublishedOrDraftToggle
              published={published}
              setPublished={setPublished}
            />
          )}
        </Stack>
      </Grid>

      {loading ? (
        <GeneralLoading />
      ) : (
        quizGroups.map((quizGroup) => (
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
            key={quizGroup.id}
            sx={{
              display: "flex",
            }}
          >
            <TieredFeatureAccess
              accessOverride={quizGroup.canAccessOnTier}
              tooltipMessage="Upgrade to unlock historic quizzes"
            >
              <QuizGroupSummaryCard
                quizGroup={quizGroup}
                showAnswerCount={showAnswerCount}
                studentView={studentView}
              />
            </TieredFeatureAccess>
          </Grid>
        ))
      )}

      {page && pageInfo && handlePageChange && (
        <Grid size={12}>
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              page={page}
              onChange={handlePageChange}
              count={pageInfo.hasNextPage ? page + 1 : page}
              color="primary"
            />
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
